/**
 * iter-110.11 — Share Dashboard dialog
 *
 * Hard-share a dashboard with selected users in your scope, OR send
 * external email invites to people who don't have an account yet.
 *
 *   • Scoped user list comes from GET /dashboards/{id}/share-targets:
 *       super_admin → every user across tenants
 *       org_admin   → users in the same org
 *       others      → users in the same tenant
 *   • Permission per share — view (default) or edit.
 *   • External emails  → comma-separated; backend sends an invite mail.
 *   • Existing shares are listed with a "Revoke" button.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Search, X, UserPlus, Loader2, Mail, ShieldCheck } from "lucide-react";
import { roleLabel } from "@/lib/terminology";

export default function ShareDashboardDialog({ open, onClose, dashboardId, dashboardName, onShared }) {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [users, setUsers] = useState([]);
    const [shares, setShares] = useState([]); // existing shared_with_users
    const [picked, setPicked] = useState([]); // user ids to add
    const [emails, setEmails] = useState("");
    const [permission, setPermission] = useState("view");
    const [query, setQuery] = useState("");

    const refresh = async () => {
        if (!dashboardId) return;
        setLoading(true);
        try {
            const r = await api.get(`/dashboards/${dashboardId}/share-targets`);
            setUsers(r.data?.users || []);
            setShares(r.data?.already_shared_with || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load people to share with");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setPicked([]);
            setEmails("");
            setQuery("");
            setPermission("view");
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dashboardId]);

    const sharedIds = useMemo(() => new Set(shares.map((s) => s.user_id)), [shares]);
    const candidates = useMemo(() => {
        const q = query.toLowerCase();
        return users
            .filter((u) => !sharedIds.has(u.id))
            .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
    }, [users, sharedIds, query]);

    const togglePick = (uid) => {
        setPicked((p) => p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]);
    };

    const submitShare = async () => {
        const emailList = (emails || "")
            .split(/[,\n;\s]+/)
            .map((e) => e.trim())
            .filter((e) => e && e.includes("@"));
        if (picked.length === 0 && emailList.length === 0) {
            toast.info("Pick at least one person or enter an email address");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post(`/dashboards/${dashboardId}/share`, {
                user_ids: picked,
                emails: emailList,
                permission,
            });
            const sharedCount = picked.length;
            const sentCount = (r.data?.emails_sent || []).length;
            toast.success("Dashboard shared", {
                description: [
                    sharedCount ? `${sharedCount} ${sharedCount === 1 ? "person" : "people"} can now ${permission === "edit" ? "edit" : "view"}` : null,
                    sentCount ? `${sentCount} invite${sentCount === 1 ? "" : "s"} emailed` : null,
                ].filter(Boolean).join(" · ") || "Updated",
            });
            setShares(r.data?.shared_with_users || []);
            setPicked([]);
            setEmails("");
            if (onShared) onShared(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Share failed");
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (uid) => {
        setBusy(true);
        try {
            const r = await api.post(`/dashboards/${dashboardId}/share`, {
                revoke_user_ids: [uid],
            });
            setShares(r.data?.shared_with_users || []);
            toast.success("Access revoked");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Revoke failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="share-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus className="h-4 w-4" /> Share &ldquo;{dashboardName || "dashboard"}&rdquo;
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-auto space-y-5 px-1">
                    {/* Existing shares */}
                    {shares.length > 0 && (
                        <div data-testid="share-existing-list">
                            <div className="text-xs font-mono text-muted-foreground uppercase mb-2">
                                People with access &middot; {shares.length}
                            </div>
                            <div className="space-y-1.5">
                                {shares.map((s) => (
                                    <div key={s.user_id} className="flex items-center gap-2 px-3 py-2 rounded-sm border border-border"
                                         data-testid={`share-existing-${s.user_id}`}>
                                        <div className="w-7 h-7 rounded-full bg-muted text-xs font-medium flex items-center justify-center shrink-0">
                                            {(s.name || s.email || "?")[0].toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm truncate">{s.name || s.email}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">{s.email}</div>
                                        </div>
                                        <Badge variant="outline" className="font-mono text-[10px] uppercase">
                                            {s.permission || "view"}
                                        </Badge>
                                        <Button size="sm" variant="ghost" className="h-7 rounded-sm"
                                                onClick={() => revoke(s.user_id)}
                                                disabled={busy}
                                                data-testid={`share-revoke-${s.user_id}`}>
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add new */}
                    <div>
                        <div className="text-xs font-mono text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                            <ShieldCheck className="h-3 w-3" /> Add people
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                <Input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name or email..."
                                    className="pl-9 h-9 rounded-sm"
                                    data-testid="share-search"
                                />
                            </div>
                            <Select value={permission} onValueChange={setPermission}>
                                <SelectTrigger className="h-9 w-32 rounded-sm text-xs" data-testid="share-permission">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="view" data-testid="share-permission-view">Can view</SelectItem>
                                    <SelectItem value="edit" data-testid="share-permission-edit">Can edit</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="border border-border rounded-sm max-h-56 overflow-auto" data-testid="share-candidates">
                            {loading && (
                                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground p-6">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
                                </div>
                            )}
                            {!loading && candidates.length === 0 && (
                                <div className="text-center text-xs text-muted-foreground p-6" data-testid="share-no-candidates">
                                    {users.length === 0 ? "No other users in your scope yet." : "No matches"}
                                </div>
                            )}
                            {!loading && candidates.map((u) => (
                                <label key={u.id}
                                       className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent/40 ${picked.includes(u.id) ? "bg-accent/60" : ""}`}
                                       data-testid={`share-candidate-${u.id}`}>
                                    <input type="checkbox"
                                           checked={picked.includes(u.id)}
                                           onChange={() => togglePick(u.id)}
                                           className="h-3.5 w-3.5 accent-primary"
                                           data-testid={`share-pick-${u.id}`} />
                                    <div className="w-7 h-7 rounded-full bg-muted text-xs font-medium flex items-center justify-center shrink-0">
                                        {(u.name || u.email || "?")[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm truncate">{u.name || u.email}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">
                                            {u.email} &middot; <span className="font-mono uppercase">{roleLabel(u.role)}</span>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* External emails */}
                    <div>
                        <div className="text-xs font-mono text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                            <Mail className="h-3 w-3" /> Or invite by email
                        </div>
                        <Input
                            value={emails}
                            onChange={(e) => setEmails(e.target.value)}
                            placeholder="alice@partner.com, bob@vendor.io"
                            className="h-9 rounded-sm"
                            data-testid="share-emails-input"
                        />
                        <div className="text-[11px] text-muted-foreground mt-1.5">
                            They'll receive an email invite. Comma-separate multiple addresses.
                        </div>
                    </div>
                </div>

                <DialogFooter className="pt-3 border-t border-border">
                    <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="share-cancel">
                        Cancel
                    </Button>
                    <Button className="rounded-sm" onClick={submitShare} disabled={busy} data-testid="share-submit">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}
                        Share
                        {picked.length > 0 && (
                            <Badge variant="secondary" className="ml-2 font-mono text-[10px]">{picked.length}</Badge>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
